import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAX_PAGE_SIZE } from "../../../../utils/formatters";

const isSameArray = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);
import { useToastStore } from "../../../../stores/toastStore";
import { DataTable, cycleSortDir, type Column } from "../../../components/DataTable";
import { TableToolbar } from "../../../components/TableToolbar";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import type { AccessAction } from "../AccessActionDialog";
import { reviewStatusLabel } from "../constants/pagConstants";
import {
  hasOpenReviewRequest,
  normalizePaginatedResponse,
  pagErrorMessage,
  sortAccountsByGroup,
} from "../helpers/pagHelpers";
import {
  fetchPagAccountFilters,
  fetchPagAccountList,
  fetchPagAccountsByGroup,
  type PagAccountFiltersResponse,
  type PagAccountListRequest,
  type PagAccountRow,
} from "../services/pagApi";
import { AccessActionButtons } from "./AccessActionButtons";

/** Which chart or breakdown was clicked, and the raw value the API should filter on. */
export interface PagChartDrilldown {
  scope: "risk" | "pam" | "group" | "group-pam" | "all" | "inventory-group";
  /** Value as the API knows it, e.g. "HIGH", "Vaulted", "Domain Admins". */
  value: string;
  /** Value as the chart showed it, for the dialog heading. */
  label: string;
  chartTitle: string;
  count: number;
  sourceSystemIds?: number[];
}

const DRILLDOWN_FILTER = (
  drilldown: PagChartDrilldown,
): Partial<PagAccountListRequest> => {
  if (drilldown.scope === "all" || drilldown.scope === "inventory-group") return {};
  if (drilldown.scope === "risk") return { riskLevels: [drilldown.value] };
  if (drilldown.scope === "pam") return { pamStatuses: [drilldown.value] };
  if (drilldown.scope === "group-pam") {
    return { pamStatuses: ["Vaulted"] };
  }
  return {};
};

const DrilldownBody = ({
  drilldown,
  sourceSystemIds: propSourceSystemIds,
  columns,
  refreshToken,
  initialFilterOptionsData,
  onRequestAction,
}: {
  drilldown: PagChartDrilldown;
  sourceSystemIds: number[];
  columns: Column<PagAccountRow>[];
  refreshToken: number;
  initialFilterOptionsData?: PagAccountFiltersResponse | null;
  onRequestAction?: (action: AccessAction, accountNames: string[]) => void;
}) => {
  const sourceSystemIds = useMemo(
    () =>
      drilldown.sourceSystemIds && drilldown.sourceSystemIds.length > 0
        ? drilldown.sourceSystemIds
        : propSourceSystemIds,
    [drilldown.sourceSystemIds, propSourceSystemIds],
  );
  const [rows, setRows] = useState<PagAccountRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [sortKey, setSortKey] = useState<string | null>("accountName");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  /* Account names, the shape the review-action endpoint takes. */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const inFlightRef = useRef(false);
  const lastFetchKeyRef = useRef("");
  const [riskFilters, setRiskFilters] = useState<string[]>(
    drilldown.scope === "risk" ? [drilldown.value] : [],
  );
  const [groupFilters, setGroupFilters] = useState<string[]>(
    drilldown.scope === "group" ||
    drilldown.scope === "group-pam" ||
    drilldown.scope === "inventory-group"
      ? [drilldown.value]
      : [],
  );
  const [pamFilters, setPamFilters] = useState<string[]>(
    drilldown.scope === "pam"
      ? [drilldown.value]
      : drilldown.scope === "group-pam"
        ? ["Vaulted"]
        : [],
  );
  const [reviewFilters, setReviewFilters] = useState<string[]>([]);
  const [filterOptionsData, setFilterOptionsData] = useState<PagAccountFiltersResponse | null>(
    initialFilterOptionsData ?? null,
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (!value.trim()) {
      setAppliedSearch("");
      setPage(0);
    }
  };

  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === appliedSearch) return;
    setAppliedSearch(next);
    setPage(0);
  };

  const isBreakdownDrilldown =
    drilldown.scope === "inventory-group" ||
    drilldown.scope === "group" ||
    drilldown.scope === "group-pam" ||
    drilldown.chartTitle.toLowerCase().includes("account inventory breakdown") ||
    drilldown.chartTitle.toLowerCase().includes("privileged account breakdown");

  const hideGroupFilter = isBreakdownDrilldown;
  const hideReviewFilter = isBreakdownDrilldown || !onRequestAction;
  const hideRiskFilter =
    drilldown.scope === "risk" ||
    drilldown.chartTitle.toLowerCase().includes("risk level");
  const hidePamFilter =
    drilldown.scope === "pam" ||
    drilldown.scope === "group-pam" ||
    drilldown.chartTitle.toLowerCase().includes("pam coverage");

  useEffect(() => {
    const nextRisk = drilldown.scope === "risk" ? [drilldown.value] : [];
    const nextGroup =
      drilldown.scope === "group" ||
      drilldown.scope === "group-pam" ||
      drilldown.scope === "inventory-group"
        ? [drilldown.value]
        : [];
    const nextPam =
      drilldown.scope === "pam"
        ? [drilldown.value]
        : drilldown.scope === "group-pam"
          ? ["Vaulted"]
          : [];

    setRiskFilters((prev) => (isSameArray(prev, nextRisk) ? prev : nextRisk));
    setGroupFilters((prev) => (isSameArray(prev, nextGroup) ? prev : nextGroup));
    setPamFilters((prev) => (isSameArray(prev, nextPam) ? prev : nextPam));
    setReviewFilters((prev) => (prev.length === 0 ? prev : []));
    setPage((prev) => (prev === 0 ? prev : 0));
  }, [drilldown.scope, drilldown.value]);

  useEffect(() => {
    if (initialFilterOptionsData) {
      setFilterOptionsData(initialFilterOptionsData);
    }
  }, [initialFilterOptionsData]);

  useEffect(() => {
    // Avoid unnecessary /filters call if initial data is present, or for breakdown drilldowns where filters are hidden
    if (initialFilterOptionsData || isBreakdownDrilldown || !sourceSystemIds.length) return;
    let cancelled = false;
    fetchPagAccountFilters(sourceSystemIds)
      .then((res) => {
        if (!cancelled && res) setFilterOptionsData(res);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sourceSystemIds, initialFilterOptionsData, isBreakdownDrilldown]);

  const riskFilterOptions = useMemo(() => {
    if (filterOptionsData?.riskLevels?.length) {
      return filterOptionsData.riskLevels.map((lvl) => {
        const upper = lvl.toUpperCase();
        const value = upper === "UNASSIGNED" ? "UNCATEGORISED" : upper;
        const label =
          upper === "UNCATEGORISED" || upper === "UNASSIGNED"
            ? "Unassigned"
            : lvl.charAt(0).toUpperCase() + lvl.slice(1).toLowerCase();
        return { value, label };
      });
    }
    return [];
  }, [filterOptionsData]);

  const groupFilterOptions = useMemo(() => {
    if (filterOptionsData?.groupNames?.length) {
      return filterOptionsData.groupNames
        .filter((name) => name && name.trim() && !name.toLowerCase().includes("not in any group"))
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
        .map((name) => ({ value: name, label: name }));
    }
    return [];
  }, [filterOptionsData]);

  const pamFilterOptions = useMemo(() => {
    if (filterOptionsData?.pamStatuses?.length) {
      return filterOptionsData.pamStatuses
        .filter((st) => st && st.trim())
        .map((st) => ({
          value: st,
          label: st,
        }));
    }
    return [];
  }, [filterOptionsData]);

  const reviewFilterOptions = useMemo(() => {
    if (filterOptionsData?.reviewStatuses?.length) {
      return filterOptionsData.reviewStatuses.map((st) => ({
        value: st,
        label: reviewStatusLabel(st),
      }));
    }
    return [];
  }, [filterOptionsData]);

  const load = useCallback(async () => {
    const isInventoryGroup =
      drilldown.scope === "inventory-group" ||
      drilldown.chartTitle.toLowerCase().includes("account inventory breakdown");

    const targetGroupName =
      groupFilters.length === 1
        ? groupFilters[0]
        : isInventoryGroup && drilldown.value
          ? drilldown.value
          : null;

    const fetchKey = `${sourceSystemIds.join(",")}|${targetGroupName ?? ""}|${appliedSearch}|${sortKey}|${sortDir}|${page}|${pageSize}|${riskFilters.join(",")}|${pamFilters.join(",")}|${groupFilters.join(",")}|${reviewFilters.join(",")}|${refreshToken}`;

    if (inFlightRef.current && lastFetchKeyRef.current === fetchKey) {
      return;
    }
    lastFetchKeyRef.current = fetchKey;
    inFlightRef.current = true;
    setLoading(true);
    try {
      const useGroupEndpoint =
        isInventoryGroup &&
        Boolean(targetGroupName) &&
        !riskFilters.length &&
        !pamFilters.length &&
        !reviewFilters.length;

      const result = useGroupEndpoint
        ? await fetchPagAccountsByGroup(
          {
            sourceSystemIds,
            groupName: targetGroupName!,
            search: appliedSearch || null,
            sortBy: sortKey ?? "accountName",
            sortDir,
          },
          page,
          pageSize,
        )
        : await fetchPagAccountList(
          {
            sourceSystemIds,
            search: appliedSearch || null,
            sortBy: sortKey ?? "accountName",
            sortDir,
            ...DRILLDOWN_FILTER(drilldown),
            ...(riskFilters.length ? { riskLevels: riskFilters } : {}),
            ...(pamFilters.length ? { pamStatuses: pamFilters } : {}),
            ...(groupFilters.length
              ? {
                  groupNames: groupFilters,
                  ...(groupFilters.length === 1 ? { groupName: groupFilters[0] } : {}),
                }
              : isInventoryGroup && drilldown.value
                ? {
                    groupNames: [drilldown.value],
                    groupName: drilldown.value,
                  }
                : {}),
            ...(hideReviewFilter || !reviewFilters.length ? {} : { reviewStatuses: reviewFilters }),
          },
          page,
          pageSize,
        );

      const { rows: content, total: totalElements } =
        normalizePaginatedResponse<PagAccountRow>(result);

      setRows(content);
      setTotal(totalElements);
      /* Rows just changed under it, so a carried-over selection would point
         at accounts that are no longer on screen. */
      setSelectedIds([]);
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Could not load these accounts",
          pagErrorMessage(err, "Please retry in a moment."),
        );
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [
    drilldown,
    sourceSystemIds,
    page,
    pageSize,
    appliedSearch,
    sortKey,
    sortDir,
    refreshToken,
    riskFilters,
    pamFilters,
    groupFilters,
    reviewFilters,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredRows = useMemo(() => {
    if (!rows.length) return rows;
    if (!groupFilters.length) return rows;
    return rows.filter((acc) =>
      acc.groupNames && Array.isArray(acc.groupNames)
        ? acc.groupNames.some((g) => groupFilters.includes(g))
        : false,
    );
  }, [rows, groupFilters]);

  const visibleRows = useMemo(() => {
    if (!filteredRows.length) return filteredRows;
    if (sortKey === "groupNames") {
      return sortAccountsByGroup(filteredRows, sortDir);
    }
    return filteredRows;
  }, [filteredRows, sortKey, sortDir]);

  const displayedTotal = useMemo(() => {
    if (groupFilters.length > 0 && rows.length > filteredRows.length) {
      return filteredRows.length;
    }
    return total;
  }, [groupFilters, rows, filteredRows, total]);

  /* Same rule as the inventory table: a row with a request already open is
     not a valid target for another one. */
  const selectableRows = useMemo(
    () => visibleRows.filter((row) => !hasOpenReviewRequest(row.reviewStatus)),
    [visibleRows],
  );
  const allSelected =
    selectableRows.length > 0 && selectedIds.length === selectableRows.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleRow = (name: string) =>
    setSelectedIds((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name],
    );

  const selectableColumns = useMemo<Column<PagAccountRow>[]>(
    () => {
      if (!onRequestAction) return columns;
      return [
        {
          key: "select",
          header: (
            <div className="flex items-center justify-center p-0.5">
              <Checkbox
                checked={allSelected ? true : someSelected ? "indeterminate" : false}
                onCheckedChange={() =>
                  setSelectedIds(allSelected ? [] : selectableRows?.map((row) => row.accountName))
                }
                disabled={selectableRows.length === 0}
                aria-label="Select all accounts without an open review"
              />
            </div>
          ),
          align: "center",
          headerAlign: "center",
          width: "64px",
          minWidth: 64,
          headerClassName: "!px-2",
          cellClassName: "!px-2 !overflow-visible",
          tooltip: false,
          render: (row) => (
            <div className="flex items-center justify-center p-0.5">
              <Checkbox
                checked={selectedIds.includes(row.accountName)}
                onCheckedChange={() => toggleRow(row.accountName)}
                disabled={hasOpenReviewRequest(row.reviewStatus)}
                aria-label={`Select ${row.accountName}`}
              />
            </div>
          ),
        },
        ...columns,
      ];
    },
    [columns, selectableRows, selectedIds, allSelected, someSelected, onRequestAction],
  );

  const handleSort = (key: string) => {
    const next = cycleSortDir(sortKey, sortDir, key);
    setLoading(true);
    setSortKey(next.sortKey);
    setSortDir(next.sortDir);
    setPage(0);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3">
      <div className="shrink-0">
        <TableToolbar
          className="max-h-fit"
          search={{
            value: search,
            onChange: handleSearchChange,
            onSubmit: submitSearch,
            placeholder: "Search",
            maxLength: 80,
            ariaLabel: `Search accounts in ${drilldown.label}`,
          }}
          pageSize={pageSize}
          pageSizeLabel="Rows per page"
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          filters={[
            {
              key: "risk",
              leadingLabel: "Risk",
              multiple: true,
              hidden: hideRiskFilter,
              values: riskFilters,
              onValuesChange: (values) => {
                setRiskFilters(values);
                setPage(0);
              },
              placeholder: "",
              options: riskFilterOptions,
            },
            {
              key: "groupName",
              leadingLabel: "Group",
              multiple: true,
              searchable: true,
              hidden: hideGroupFilter,
              values: groupFilters,
              onValuesChange: (values) => {
                setGroupFilters(values);
                setPage(0);
              },
              placeholder: "",
              options: groupFilterOptions,
            },
            {
              key: "pamStatus",
              leadingLabel: "PAM Status",
              multiple: true,
              hidden: hidePamFilter,
              values: pamFilters,
              onValuesChange: (values) => {
                setPamFilters(values);
                setPage(0);
              },
              placeholder: "",
              options: pamFilterOptions,
            },
            {
              key: "review",
              leadingLabel: "Review",
              multiple: true,
              hidden: hideReviewFilter,
              values: reviewFilters,
              onValuesChange: (values) => {
                setReviewFilters(values);
                setPage(0);
              },
              placeholder: "",
              options: reviewFilterOptions,
            },
          ]}
          actions={
            onRequestAction ? (
              <AccessActionButtons
                selectedCount={selectedIds.length}
                onApprove={() => onRequestAction("review", selectedIds)}
                onDeny={() => onRequestAction("revoke", selectedIds)}
              />
            ) : null
          }
        />
      </div>

      <div className="flex-1 min-h-0">
        <DataTable
          columns={selectableColumns}
          data={visibleRows}
          keyField="accountName"
          loading={loading}
          page={page}
          pageSize={pageSize}
          totalItems={displayedTotal}
          onPageChange={setPage}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          itemLabel="accounts"
          emptyMessage="No Data Available"
          maxHeight="calc(100vh - 220px)"
        />
      </div>
    </div>
  );
};

export const PagChartDrilldownDrawer = ({
  drilldown,
  sourceSystemIds,
  columns,
  refreshToken,
  filterOptionsData,
  onRequestAction,
  onClose,
}: {
  drilldown: PagChartDrilldown | null;
  sourceSystemIds: number[];
  columns: Column<PagAccountRow>[];
  /** Bumped by the page after a review or revoke lands, to re-read the list. */
  refreshToken: number;
  filterOptionsData?: PagAccountFiltersResponse | null;
  onRequestAction?: (action: AccessAction, accountNames: string[]) => void;
  onClose: () => void;
}) => (
  <Sheet
    open={drilldown !== null}
    onOpenChange={(next) => {
      if (!next) onClose();
    }}
  >
    <SheetContent
      side="right"
      className="flex h-full max-h-screen w-[90vw] max-w-none flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-none"
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <SheetHeader className="gap-1.5 border-b border-border p-5 pr-14 text-left shrink-0">
        <SheetTitle className="text-base font-semibold">
          {drilldown?.label ?? ""}
        </SheetTitle>
        <SheetDescription className="text-xs text-muted-foreground">
          {drilldown
            ? `${drilldown.chartTitle} - ${drilldown.count} account${drilldown.count === 1 ? "" : "s"}`
            : ""}
        </SheetDescription>
      </SheetHeader>
      <div className="min-h-0 flex-1 flex flex-col p-5 overflow-hidden">
        {drilldown ? (
          <DrilldownBody
            key={`${drilldown.scope}:${drilldown.value}:${drilldown.sourceSystemIds?.join(",") ?? ""}`}
            drilldown={drilldown}
            sourceSystemIds={sourceSystemIds}
            columns={columns}
            refreshToken={refreshToken}
            initialFilterOptionsData={filterOptionsData}
            onRequestAction={onRequestAction}
          />
        ) : null}
      </div>
    </SheetContent>
  </Sheet>
);
